import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { api, type DeckComment, type DirectoryPerson } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** Pull @tokens that look like emails or first-word names for matching. */
function extractMentionTokens(text: string): string[] {
  return [...text.matchAll(/@([^\s@]+)/g)].map((m) => m[1]!.toLowerCase());
}

function resolveMentions(text: string, people: DirectoryPerson[]): string[] {
  const tokens = extractMentionTokens(text);
  if (!tokens.length) return [];
  const emails = new Set<string>();
  for (const token of tokens) {
    const byEmail = people.find((p) => p.email.toLowerCase() === token || p.email.toLowerCase().startsWith(token));
    if (byEmail) {
      emails.add(byEmail.email.toLowerCase());
      continue;
    }
    const byName = people.find((p) => p.name.toLowerCase().split(/\s+/)[0] === token || p.name.toLowerCase().includes(token));
    if (byName) emails.add(byName.email.toLowerCase());
  }
  return [...emails];
}

export function DeckComments({
  deckId,
  canComment,
  isOwner,
}: {
  deckId: string;
  canComment: boolean;
  isOwner: boolean;
}) {
  const me = useAuthStore((s) => s.user);
  const [comments, setComments] = useState<DeckComment[]>([]);
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(
    () =>
      Promise.all([api.listComments(deckId), api.directory().catch(() => ({ people: [] }))])
        .then(([{ comments: list }, dir]) => {
          setComments(list);
          setPeople(dir.people);
        })
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not load comments'))
        .finally(() => setLoading(false)),
    [deckId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const mentionQuery = useMemo(() => {
    const m = text.match(/@([^\s@]*)$/);
    return m ? m[1]!.toLowerCase() : null;
  }, [text]);

  // Suggestions show while the text ends in an unfinished @mention.
  const mentionOpen = mentionQuery !== null;

  const mentionSuggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    return people
      .filter(
        (p) =>
          p.email.toLowerCase().includes(mentionQuery) ||
          p.name.toLowerCase().includes(mentionQuery),
      )
      .slice(0, 6);
  }, [people, mentionQuery]);

  const visible = comments.filter((c) => (showResolved ? true : !c.resolved));
  const resolvedCount = comments.filter((c) => c.resolved).length;

  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      const mentions = resolveMentions(body, people);
      const { comment } = await api.addComment(deckId, body, mentions);
      setComments((prev) => [...prev, comment]);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post comment');
    } finally {
      setBusy(false);
    }
  };

  const toggleResolved = async (c: DeckComment) => {
    setBusy(true);
    setError(null);
    try {
      const { comment } = await api.resolveComment(deckId, c.id, !c.resolved);
      setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...comment } : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update comment');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteComment(deckId, id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete comment');
    } finally {
      setBusy(false);
    }
  };

  const insertMention = (person: DirectoryPerson) => {
    setText((prev) => prev.replace(/@([^\s@]*)$/, `@${person.email} `));
  };

  const canDelete = (c: DeckComment) => {
    if (!me) return false;
    if (me.role === 'admin' || isOwner) return true;
    return c.author_email.toLowerCase() === me.email.toLowerCase();
  };

  const canResolve = (c: DeckComment) => {
    if (!me) return false;
    if (me.role === 'admin' || isOwner) return true;
    return c.author_email.toLowerCase() === me.email.toLowerCase();
  };

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderLeft: 1,
        borderColor: 'divider',
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        bgcolor: 'background.paper',
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2">Comments</Typography>
          {resolvedCount > 0 && (
            <Button size="small" color="neutral" onClick={() => setShowResolved((v) => !v)}>
              {showResolved ? 'Hide resolved' : `Show resolved (${resolvedCount})`}
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {canComment ? 'Use @email to mention colleagues' : 'Share the deck to enable comments'}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={24} aria-label="Loading comments" />
          </Box>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {comments.length ? 'All comments are resolved.' : 'No comments yet.'}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {visible.map((c) => (
              <Box
                key={c.id}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: c.resolved ? 'action.selected' : 'action.hover',
                  opacity: c.resolved ? 0.75 : 1,
                }}
              >
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {c.author_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {formatWhen(c.created_at)}
                      {c.resolved ? ' · Resolved' : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {c.body}
                    </Typography>
                    {c.mentions?.length > 0 && (
                      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
                        {c.mentions.map((m) => (
                          <Chip key={m} size="small" label={`@${m}`} sx={{ height: 20, fontSize: 11 }} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {canResolve(c) && (
                    <IconButton
                      size="small"
                      aria-label={c.resolved ? 'Reopen comment' : 'Resolve comment'}
                      disabled={busy}
                      onClick={() => void toggleResolved(c)}
                    >
                      {c.resolved ? (
                        <RadioButtonUncheckedIcon fontSize="small" />
                      ) : (
                        <CheckCircleOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                  {canDelete(c) && (
                    <IconButton
                      size="small"
                      aria-label="Delete comment"
                      disabled={busy}
                      onClick={() => void remove(c.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {canComment && (
        <Stack spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider', position: 'relative' }}>
          {mentionOpen && mentionSuggestions.length > 0 && (
            <List
              dense
              disablePadding
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 16,
                right: 16,
                mb: 0.5,
                maxHeight: 180,
                overflow: 'auto',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                zIndex: 2,
              }}
            >
              {mentionSuggestions.map((p) => (
                <ListItemButton key={p.email} onClick={() => insertMention(p)}>
                  <ListItemText primary={p.name} secondary={p.email} />
                </ListItemButton>
              ))}
            </List>
          )}
          <TextField
            multiline
            minRows={2}
            maxRows={5}
            placeholder="Add a comment… use @ to mention"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            size="small"
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={busy || !text.trim()}
            onClick={() => void submit()}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Post
          </Button>
        </Stack>
      )}
    </Box>
  );
}
